// "Log in with Telegram" for a plain browser: the popup library of
// `ai/references/https/core.telegram.org/bots/telegram-login.md` hands back an OpenID Connect id_token, which the
// backend (`/auth/telegram-login`) verifies and exchanges for the app's own session.

const LIBRARY_URL = 'https://oauth.telegram.org/js/telegram-login.js?3'

interface LoginResult {
  id_token?: string
  user?: unknown
  error?: string
}

interface TelegramLoginApi {
  auth(
    options: { client_id: number; scope?: string[]; lang?: string; nonce?: string },
    callback: (result?: LoginResult) => void,
  ): void
}

// (`window.Telegram` is typed for the Mini App SDK in `telegramWebApp.ts`, the login library adds `Login` to it)
function loginApi(): TelegramLoginApi | undefined {
  return (window as unknown as { Telegram?: { Login?: TelegramLoginApi } }).Telegram?.Login
}

// The user closed the popup (or never finished): not an error worth reporting.
export class LoginCancelledError extends Error {}
// Telegram refused the login, or the library is not usable.
export class LoginFailedError extends Error {}

export function idTokenFromLoginResult(result: LoginResult | undefined): string {
  if (result?.id_token) return result.id_token
  if (result?.error === 'popup_closed') throw new LoginCancelledError('The login popup was closed.')
  throw new LoginFailedError(result?.error || 'Telegram did not return a login token.')
}

let loading: Promise<void> | null = null

// Browsers only allow a popup straight from a click, so the library has to be loaded *before* the button is
// pressed: call this when the page shows the button, `requestTelegramLogin` then runs synchronously in the click.
export function loadTelegramLogin(): Promise<void> {
  if (loginApi()) return Promise.resolve()
  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = LIBRARY_URL
    script.async = true
    script.onload = () =>
      loginApi() ? resolve() : reject(new LoginFailedError('Login library missing.'))
    script.onerror = () => {
      loading = null
      script.remove()
      reject(new LoginFailedError('Could not load the Telegram login library.'))
    }
    document.head.appendChild(script)
  })
  return loading
}

export function requestTelegramLogin(options: {
  clientId: number
  nonce: string
  lang?: string
}): Promise<string> {
  return new Promise((resolve, reject) => {
    const login = loginApi()
    if (!login) {
      reject(new LoginFailedError('The Telegram login library is not loaded.'))
      return
    }
    login.auth(
      { client_id: options.clientId, scope: ['profile'], nonce: options.nonce, lang: options.lang },
      (result) => {
        try {
          resolve(idTokenFromLoginResult(result))
        } catch (e) {
          reject(e)
        }
      },
    )
  })
}
